import * as React from "react";
import type { DemoMode } from "./DemoModeSwitcher";
import demoHints from "./demo_hints.json";
import { peekTourHeldProfileIds } from "./processingHold";
import {
  clearTourProgress,
  readTourProgress,
  writeTourProgress,
} from "./tourProgress";
import {
  snapTourStep,
  stepIndexForId,
  type TourWorld,
} from "./snapTourStep";
import type { DemoHintStep } from "./types";

const steps = (demoHints as { steps: DemoHintStep[] }).steps;

/** Max wait for a step target to appear in the DOM before ending the tour. */
export const TOUR_TARGET_WAIT_MS = 10_000;

/** Click steps that navigate away — advance is handled by the target's onClick. */
const NAVIGATION_CLICK_STEP_IDS = new Set(["dashboard-details"]);

const EMPTY_WORLD: TourWorld = {
  hasSelectedProfile: false,
  hasProfileContent: false,
  hasMeetingResults: false,
  isPlaybackActive: false,
  isOnDashboard: false,
};

export type UseDemoTourOptions = {
  isPlaybackActive?: boolean;
  username?: string;
  /** Current UI world for snapTourStep. */
  world?: TourWorld;
  /**
   * False until profiles/meta are loaded enough to snap safely.
   * Defaults to true when omitted (tests / simple callers).
   */
  worldReady?: boolean;
  /** Recsys-only: reveal profiles hidden during the processing hold. */
  revealHeldProfiles?: () => void | Promise<void>;
};

export type UseDemoTourResult = {
  isRunning: boolean;
  /** True while the tour waits for recording to end before showing the next hint. */
  isPaused: boolean;
  currentStep: DemoHintStep | null;
  stepIndex: number;
  stepCount: number;
  targetElement: HTMLElement | null;
  /** True while the current step has no connected target yet. */
  isWaitingForTarget: boolean;
  /** Saved progress exists (paused or mid-tour) — show Continue. */
  canResume: boolean;
  startTour: () => void;
  restartTour: () => void;
  skipTour: () => void;
  nextStep: () => void;
  onDemoModeChange: (mode: DemoMode) => void;
  registerTarget: (id: string, el: HTMLElement | null) => void;
};

export function useDemoTour(
  demoMode: DemoMode,
  setDemoMode: (mode: DemoMode) => void,
  options?: UseDemoTourOptions,
): UseDemoTourResult {
  const isPlaybackActive = Boolean(options?.isPlaybackActive);
  const revealHeldProfiles = options?.revealHeldProfiles;
  const username = options?.username ?? "";
  const world = options?.world ?? EMPTY_WORLD;
  const worldReady = options?.worldReady !== false;

  const isPlaybackActiveRef = React.useRef(isPlaybackActive);
  React.useEffect(() => {
    isPlaybackActiveRef.current = isPlaybackActive;
  }, [isPlaybackActive]);

  const worldRef = React.useRef(world);
  React.useEffect(() => {
    worldRef.current = world;
  }, [world]);

  const usernameRef = React.useRef(username);
  React.useEffect(() => {
    usernameRef.current = username;
  }, [username]);

  const [isRunning, setIsRunning] = React.useState(false);
  const [isPaused, setIsPaused] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [canResume, setCanResume] = React.useState(false);
  const [targets, setTargets] = React.useState<ReadonlyMap<string, HTMLElement>>(
    () => new Map(),
  );
  const targetsRef = React.useRef(targets);
  React.useEffect(() => {
    targetsRef.current = targets;
  }, [targets]);
  const runningHydratedRef = React.useRef(false);
  const stepHydratedRef = React.useRef(false);

  const currentStep =
    isRunning && !isPaused ? (steps[stepIndex] ?? null) : null;
  const stepCount = steps.length;
  const rawTarget = currentStep ? (targets.get(currentStep.target) ?? null) : null;
  const targetElement = rawTarget?.isConnected ? rawTarget : null;
  const isWaitingForTarget = Boolean(isRunning && currentStep && !targetElement);

  const persist = React.useCallback(
    (next: { stepId: string; running: boolean } | null) => {
      const user = usernameRef.current;
      if (!user) return;
      if (!next) {
        clearTourProgress(user);
        setCanResume(false);
        return;
      }
      writeTourProgress(user, next);
      setCanResume(true);
    },
    [],
  );

  const enrichWorld = React.useCallback((): TourWorld => {
    const base = worldRef.current;
    const t = targetsRef.current;
    const connected = (id: string) => Boolean(t.get(id)?.isConnected);
    return {
      ...base,
      isProfileExpanded:
        base.isProfileExpanded || connected("long-recommendation"),
      isMeetingResultsOpen:
        base.isMeetingResultsOpen || connected("meeting-results-body"),
      isMeetingBreakdownOpen:
        base.isMeetingBreakdownOpen || connected("meeting-timecodes"),
      isDesignerPanelOpen:
        base.isDesignerPanelOpen ||
        connected("dashboard-metric-chart") ||
        connected("dashboard-criteria-list") ||
        connected("dashboard-details"),
      isFirstMeetingOpen:
        base.isFirstMeetingOpen ||
        connected("designer-audio") ||
        connected("designer-meeting-summary") ||
        connected("designer-meeting-flags") ||
        connected("designer-criterion") ||
        connected("designer-issues") ||
        connected("designer-improve"),
      hasCriterionTimecode:
        base.hasCriterionTimecode || connected("designer-timecode"),
    };
  }, []);

  const resolveIndex = React.useCallback(
    (stepId: string) => stepIndexForId(snapTourStep(stepId, enrichWorld())),
    [enrichWorld],
  );

  const snapId = React.useCallback(
    (stepId: string) => snapTourStep(stepId, enrichWorld()),
    [enrichWorld],
  );

  // Callback refs under Radix asChild (Tooltip/Popover) often call ref(null) then
  // ref(node) in the same turn. Synchronous delete+set would re-render forever (#185).
  const registerTarget = React.useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      setTargets((prev) => {
        if (prev.get(id) === el) return prev;
        const next = new Map(prev);
        next.set(id, el);
        return next;
      });
      return;
    }
    queueMicrotask(() => {
      setTargets((prev) => {
        const current = prev.get(id);
        if (!current) return prev;
        if (current.isConnected) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    });
  }, []);

  const revealHeld = React.useCallback(() => {
    void revealHeldProfiles?.();
  }, [revealHeldProfiles]);

  /** Hard stop: clear progress (finale / timeout / free without resume intent). */
  const endTour = React.useCallback(
    (opts?: { clearProgress?: boolean }) => {
      revealHeld();
      setIsRunning(false);
      setIsPaused(false);
      setStepIndex(0);
      setDemoMode("free");
      if (opts?.clearProgress !== false) {
        persist(null);
      }
    },
    [persist, revealHeld, setDemoMode],
  );

  const beginAtIndex = React.useCallback(
    (index: number) => {
      const safe = Math.max(0, Math.min(index, steps.length - 1));
      const stepId = steps[safe]?.id ?? steps[0]!.id;
      setDemoMode("tour");
      setStepIndex(safe);
      setIsPaused(false);
      setIsRunning(true);
      persist({ stepId, running: true });
    },
    [persist, setDemoMode],
  );

  const startTour = React.useCallback(() => {
    const saved = usernameRef.current
      ? readTourProgress(usernameRef.current)
      : null;
    if (saved) {
      beginAtIndex(resolveIndex(saved.stepId));
      return;
    }
    beginAtIndex(resolveIndex(steps[0]!.id));
  }, [beginAtIndex, resolveIndex]);

  const restartTour = React.useCallback(() => {
    persist(null);
    beginAtIndex(resolveIndex(steps[0]!.id));
  }, [beginAtIndex, persist, resolveIndex]);

  const skipTour = React.useCallback(() => {
    revealHeld();
    const stepId = steps[stepIndex]?.id ?? steps[0]!.id;
    setIsRunning(false);
    setIsPaused(false);
    setDemoMode("free");
    persist({ stepId, running: false });
  }, [persist, revealHeld, setDemoMode, stepIndex]);

  const nextStep = React.useCallback(() => {
    setStepIndex((prev) => {
      const leaving = steps[prev];
      const next = prev + 1;
      if (next >= steps.length) {
        revealHeld();
        setIsPaused(false);
        setIsRunning(false);
        setDemoMode("free");
        persist(null);
        return 0;
      }
      if (leaving?.pauseUntil === "recording-end" && isPlaybackActiveRef.current) {
        setIsPaused(true);
      }
      const nextId = steps[next]?.id;
      if (nextId) persist({ stepId: nextId, running: true });
      return next;
    });
  }, [persist, revealHeld, setDemoMode]);

  const onDemoModeChange = React.useCallback(
    (mode: DemoMode) => {
      if (mode === "tour") {
        startTour();
        return;
      }
      // Switch to free = skip (keep progress), not hard clear.
      if (isRunning) {
        skipTour();
        return;
      }
      setDemoMode("free");
    },
    [isRunning, setDemoMode, skipTour, startTour],
  );

  // Restore running tour chrome immediately so route changes do not drop the overlay.
  React.useLayoutEffect(() => {
    if (!username || runningHydratedRef.current) return;
    runningHydratedRef.current = true;
    const saved = readTourProgress(username);
    if (!saved) {
      setCanResume(false);
      return;
    }
    setCanResume(true);
    setStepIndex(stepIndexForId(saved.stepId));
    if (saved.running) {
      setDemoMode("tour");
      setIsPaused(false);
      setIsRunning(true);
    } else {
      setDemoMode("free");
      setIsRunning(false);
    }
  }, [username, setDemoMode]);

  // Snap step index once UI world is ready (panel open, detail route, etc.).
  React.useLayoutEffect(() => {
    if (!username || !worldReady || stepHydratedRef.current) return;
    stepHydratedRef.current = true;
    const saved = readTourProgress(username);
    if (!saved) return;
    const snapped = snapId(saved.stepId);
    setStepIndex(stepIndexForId(snapped));
    if (saved.running) {
      writeTourProgress(username, { stepId: snapped, running: true });
    } else if (snapped !== saved.stepId) {
      writeTourProgress(username, { stepId: snapped, running: false });
    }
  }, [username, worldReady, snapId]);

  // Refresh canResume when username appears (before running hydrate).
  React.useEffect(() => {
    if (!username) {
      setCanResume(false);
      return;
    }
    if (runningHydratedRef.current) return;
    setCanResume(readTourProgress(username) != null);
  }, [username]);

  // Resume after recording-end pause once playback is no longer active.
  React.useEffect(() => {
    if (!isRunning || !isPaused) return;
    if (isPlaybackActive) return;
    setIsPaused(false);
  }, [isRunning, isPaused, isPlaybackActive]);

  // Mid-tour reveal: when entering a content step while hold is active, show the form.
  React.useEffect(() => {
    if (!isRunning || !currentStep) return;
    if (currentStep.block !== "profile-content") return;
    if (peekTourHeldProfileIds().length === 0) return;
    revealHeld();
  }, [isRunning, currentStep, revealHeld]);

  // Gate timeout: end tour if the current step target never appears.
  // Skipped while paused (no current step / overlay).
  // Clears progress — target never appearing is a hard fail.
  React.useEffect(() => {
    if (!isWaitingForTarget) return;
    const timer = window.setTimeout(() => {
      endTour({ clearProgress: true });
    }, TOUR_TARGET_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [isWaitingForTarget, currentStep?.id, endTour]);

  // Advance on click when current step requires a click action.
  React.useEffect(() => {
    if (!isRunning || !currentStep || currentStep.action !== "click") return;
    if (NAVIGATION_CLICK_STEP_IDS.has(currentStep.id)) return;
    const el = targetElement;
    if (!el) return;

    const onClick = () => {
      nextStep();
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [isRunning, currentStep, targetElement, nextStep]);

  // Sync: if mode becomes free externally while running, treat as skip (keep progress).
  React.useEffect(() => {
    if (demoMode === "free" && isRunning) {
      revealHeld();
      const stepId = steps[stepIndex]?.id ?? steps[0]!.id;
      setIsRunning(false);
      setIsPaused(false);
      persist({ stepId, running: false });
    }
  }, [demoMode, isRunning, persist, revealHeld, stepIndex]);

  return {
    isRunning,
    isPaused,
    currentStep,
    stepIndex,
    stepCount,
    targetElement,
    isWaitingForTarget,
    canResume,
    startTour,
    restartTour,
    skipTour,
    nextStep,
    onDemoModeChange,
    registerTarget,
  };
}
